import { defineNuxtPlugin } from '#imports'

export default defineNuxtPlugin(() => {
  // OneSignal Web SDK 透過 CDN script 載入 (在 nuxt.config 或 index.html 中)
  // 此 plugin 提供 Vue instance 可用的 $OneSignal 存取

  if (!import.meta.client) return

  // 快取 OneSignal promise，避免重複建立 interval
  let oneSignalPromise = null

  // OneSignal SDK 會在 window 上掛載，等待其初始化
  const waitForOneSignal = () => {
    // 如果已經有快取的 promise，直接返回
    if (oneSignalPromise) {
      return oneSignalPromise
    }

    // 如果已經載入，直接返回
    if (window.OneSignal) {
      return Promise.resolve(window.OneSignal)
    }

    oneSignalPromise = new Promise((resolve) => {
      let checkInterval = null
      let timeoutId = null
      let resolved = false

      const cleanup = () => {
        if (checkInterval) {
          clearInterval(checkInterval)
          checkInterval = null
        }
        if (timeoutId) {
          clearTimeout(timeoutId)
          timeoutId = null
        }
      }

      const safeResolve = (value) => {
        if (resolved) return
        resolved = true
        cleanup()
        resolve(value)
      }

      checkInterval = setInterval(() => {
        if (window.OneSignal) {
          safeResolve(window.OneSignal)
        }
      }, 100)

      timeoutId = setTimeout(() => {
        console.warn('[OneSignal] SDK not loaded within 30 seconds')
        safeResolve(null)
      }, 30000)
    })

    return oneSignalPromise
  }

  // init 完成閘門：login / logout 必須等到 OneSignal.init() 完成 (SDK 核心 _coreDirector 就緒)
  // 才能呼叫，否則 SDK 內部會存取尚未建立的 _coreDirector 而拋出未捕捉的 rejection (Sentry CLIENT-PWA-SSR-RJ)
  let isInitialized = false
  let initGateSettled = false
  let resolveInitGate = null
  const initGate = new Promise((resolve) => {
    resolveInitGate = resolve
  })
  const settleInitGate = () => {
    if (initGateSettled) return
    initGateSettled = true
    resolveInitGate()
  }

  return {
    provide: {
      OneSignal: {
        async init(config) {
          const OneSignal = await waitForOneSignal()
          if (!OneSignal) {
            console.error('[OneSignal] SDK not available')
            settleInitGate()
            return
          }

          try {
            await OneSignal.init(config)
            isInitialized = true
            console.log('[OneSignal] Initialized successfully')
          } catch (error) {
            console.error('[OneSignal] Initialization error:', error)
          } finally {
            settleInitGate()
          }
        },

        async login(externalId) {
          const OneSignal = await waitForOneSignal()
          if (!OneSignal) return

          // 等待 init 完成，確保 SDK 核心已就緒；init 失敗時不呼叫 login 以免崩潰
          if (!isInitialized) await initGate
          if (!isInitialized) return

          try {
            await OneSignal.login(externalId)
            console.log(`[OneSignal] User logged in: ${externalId}`)
          } catch (error) {
            console.error('[OneSignal] Login error:', error)
          }
        },

        async logout() {
          const OneSignal = await waitForOneSignal()
          if (!OneSignal) return

          // 等待 init 完成，確保 SDK 核心已就緒；init 失敗時不呼叫 logout 以免崩潰
          if (!isInitialized) await initGate
          if (!isInitialized) return

          try {
            await OneSignal.logout()
            console.log('[OneSignal] User logged out')
          } catch (error) {
            console.error('[OneSignal] Logout error:', error)
          }
        },

        // 直接存取 OneSignal SDK
        async getSDK() {
          return await waitForOneSignal()
        },
      },
    },
  }
})
